import React, { useEffect, useRef, useState, useCallback } from "react";
import "@innovatrics/dot-document-auto-capture";
import { CameraIcon } from "@heroicons/react/24/solid";
import axios from "axios";
import Head from "../Head";
import Footer from "../../components/Footer";
import { useContext } from "react";
import GlobalConfig from "../../context/GlobalConfig";
import { languageData } from "../../constants/languageData";
import { getDocumentCountry, getDocumentIdType } from "../../utils/documentFields";
import  { documentSideConfigurationConstants }  from "../../constants/documentSideConfigConstants.js";
import focusIcon from "../../assets/focus.svg";
import DocumentUI from "./DocumentUI";
import DocumentCamera from "./DocumentCamera";
import ImageCheck from "./ImageCheck";
import "@innovatrics/dot-document-auto-capture/events";
import uploadFile from "../../assets/upload_file.svg";
import { residentIDCardName, drivingLicenceName, idCardName, countries } from "../../constants/countries";
import "./docCapture.css";
import { useParams } from "react-router-dom";
import JourneyConfig from "../../context/JourneyConfig";
import Loader from "../../components/Loader/Loader";
import CameraLoadingOverlay from "../../components/Loader/CameraLoadingOverlay";
import useDeviceDetection from "../../hooks/useDeviceDetection";
import { DocumentCustomEvent } from "@innovatrics/dot-document-auto-capture/events";
import "../../styles/Modal.css";
import { UploadStatusMessages } from "../../constants/failedUpload";
import captureDocButton from "../../assets/captureDocButton.svg";
import MobileCapture from "./MobileCapture";
import FlipScreen from "./FlipScreen.jsx";
import GoogleIcon from "../../components/GoogleIcon";

const BackDocumentCapture = ({
  stepperFunc,
  steps,
  docIteration,
  backImageRef,
  currentDocumentStep,
  currentStep,
  setCapturingMode,
  capturingMode,
  setAutoCaptureLoadingTime,
}) => {
  const { config } = useContext(GlobalConfig);
  const { journey, setJourney, getJourney } = useContext(JourneyConfig);
  const [imageUrl, setImageUrl] = useState("");
  const [captureStatus, setCaptureStatus] = useState("document_not_present");
  const { branding } = config ?? {};
  const [isLoading, setIsLoading] = useState(false);
  const { journeyId } = useParams();
  const isMobile = useDeviceDetection();
  const innovatricsCameraRef = useRef(null);
  const [loadTiming, setLoadTiming] = useState(null);
  const streamRef = useRef(null);
  const color = config?.branding?.accentColor;
  const documentCapture = Array.isArray(config?.documentCapture)
    ? config?.documentCapture[currentDocumentStep]
    : config?.documentCapture;
  const bgColor =
    "color-mix(in srgb, " + config?.branding?.accentColor + " 10%, white)";
  const [appState, setAppState] = useState();
  const env = process.env.NODE_ENV || "development";
  const language = sessionStorage.getItem("language") ?? "en";
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCameraRunning, setIsCameraRunning] = useState(false);
  const timeoutRef = useRef(null);
  const [message, setMessage] = useState("");
  const [isFlipScreenProcessed, setIsFlipScreenProcessed] = useState(() => {
    const isShown = sessionStorage.getItem('isFlipScreenShown');
    return !!isShown;
  });
  const videoRef = useRef(null); // Reference to the video element
  const canvasRef = useRef(null); // Reference to the canvas element
  const [facingMode, setFacingMode] = useState("user");
  const [width, setWidth] = useState(window.innerWidth);
  const [captureMode, setCaptureMode] = useState("auto");
  const fileInputRef = useRef(null);
  const [documentSideConfiguration,setDocumentSideConfiguration] = useState(config?.documentCapture?.[currentDocumentStep]?.documentSideConfiguration ?? documentSideConfigurationConstants.FRONT_MANDATORY_BACK_OPTIONAL);
  // This document's own country and type, not the first document's (IDX-677).
  const [countryCode] = useState(() => getDocumentCountry(journey, currentDocumentStep));
  const [countryName] = useState(
    countryCode
      ? countries?.find((country) => country.value === countryCode)?.label
      : null
  );
  const [documentName] = useState(() => getDocumentIdType(journey, currentDocumentStep));

  useEffect(() => {
    const cleanupCameras = async () => {
      try {
        sessionStorage.removeItem('isCameraPage');
        const video = videoRef?.current;
        stopSafeStreamFromRef();
        console.log('[BackCapture][cleanupCameras] stream cleared without error')
        if (video?.srcObject) {
          video.srcObject = null;
        }
      } catch (error) {
        console.log("[BackCapture][cleanupCameras] Error during clean-up : ",error)
      }
    };
    return () => {
      cleanupCameras().catch(error => {
        console.error('Cleanup error:', error);
      });
    };
  }, []); 

  useEffect(() => {
    if(isFlipScreenProcessed) sessionStorage.setItem('isCameraPage', 'true');
  }, [isFlipScreenProcessed]);
  
  useEffect(() => {
    if (isMobile) {
      setFacingMode("environment");
    }
  }, [isMobile]);

  useEffect(() => {
    if (journeyId && !journey) getJourney(journeyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [journeyId]);

  const handleFileChange = (e) => {
    try {
      // Log when file selection starts
      console.log('[BackCapture][File Upload] File selection triggered');
  
      const file = e.target.files[0];
      
      // Log basic file info
      if (file) {
        console.log('[BackCapture][File Upload] Selected file:', {
          name: file?.name,
          type: file?.type,
        });
      } else {
        console.warn('[BackCapture][File Upload] No file selected');
        return;
      }
  
      // India supports PDF uploads, same as other IDs.
      const allowedExtensions = ["image/jpeg", "image/png", "image/jpg", "application/pdf"];
      const maxSize = 5 * 1024 * 1024; // 5 MB in bytes
      setCapturingMode("UPLOAD");
      // Validate file type
      if (!allowedExtensions.includes(file.type)) {
        const errorMsg = `Invalid file type: ${file.type}. Allowed: ${allowedExtensions.join(', ')}`;
        console.error('[BackCapture][File Upload]', errorMsg);
        
        setMessage(
          languageData[language]?.failedUpload?.text ||
          UploadStatusMessages.FailedUploadText
        );
        setIsModalOpen(true);
        e.target.value = ''; // Clear the input
        return;
      }
  
      // Validate file size
      if (file.size > maxSize) {
        const sizeInMB = (file.size / (1024 * 1024)).toFixed(2);
        const errorMsg = `File too large: ${sizeInMB}MB. Max allowed: 5MB`;
        console.error('[BackCapture][File Upload]', errorMsg);
        
        setMessage(
          languageData[language]?.failedUpload?.text ||
          UploadStatusMessages.FailedUploadText
        );
        setIsModalOpen(true);
        e.target.value = ''; // Clear the input
        return;
      }
  
      // Process valid file
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result;
        e.target.value = '';
        console.log('[BackCapture][File Upload] File successfully converted to base64, length:', base64Data.length);
        setImageUrl(base64Data);
        console.log('[BackCapture][File Upload] File converted to base64 and processing completed successfully');
      };
      reader.readAsDataURL(file);
  
    } catch (error) {
      console.error('[BackCapture][File Upload] Unexpected error:', {
        name: error?.name,
        message: error.message,
        stack: error?.stack ,
        additionalInfo: {
          file: error?.fileName || undefined,
          line: error?.lineNumber || undefined,
          column: error?.columnNumber || undefined,
          // Add any other relevant context here
          timestamp: new Date().toISOString(),
          userAgent: navigator?.userAgent
        },
        error : error
      });
      e.target.value = ''
    }
  };

  const stopSafeStreamFromRef = useCallback(() => {
    console.log('[BackCapture][safeStream] Attempting to stop stream from streamRef');
  
    const stream = streamRef?.current;
    if (!stream) {
      console.log('[BackCapture][safeStream] No stream found in streamRef - skipping');
      return;
    }
  
    try {
      const tracks = stream.getTracks();
      console.log(`[BackCapture][safeStream] Found ${tracks.length} tracks to stop`);
  
      tracks.forEach((track) => {
        console.log(`[BackCapture][safeStream] Stopping ${track?.kind} track (ID: ${track?.id})`);
        track.stop();
        console.log(`[BackCapture][safeStream] Track stopped: ${track?.readyState}`);
      });
  
      streamRef.current = null;
      console.log('[BackCapture][safeStream] Stream cleanup complete and ref cleared');
    } catch (e) {
      console.error('[BackCapture][safeStream] Stream cleanup failed:', e);
    }
  }, []);    

  useEffect(() => {    
    // Set up 5-second fallback
    timeoutRef.current = setTimeout(() => {
      if (!isCameraRunning) {
        console.log('[BackCapture][CameraReadyState] ⏱️ Fallback triggered - marking camera as ready after timeout');
        setIsCameraRunning(true);
      }
    }, 5000);
  
    const handleStateChanged = (event) => {
      if (!event.detail) {
        console.warn('[BackCapture][CameraReadyState] Received state change event without details', event);
        return;
      }
             console.log(`[BackCapture][CameraReadyState] State changed: ${event?.detail?.appState}`, JSON.stringify(event?.detail));
       if (event?.detail?.appState === 'RUNNING') {
         if (loadTiming) {
           const durationInSeconds = ((performance.now() - loadTiming) / 1000).toFixed(2);
           setAutoCaptureLoadingTime(durationInSeconds);
           console.log('[BackCapture][CameraReadyState] 🎥 Camera is now operational, loading time:', durationInSeconds, 'seconds');
         } else {
           console.log('[BackCapture][CameraReadyState] 🎥 Camera is now operational but loadTiming not set yet');
         }
         if(innovatricsCameraRef.current){
           console.log('[BackCapture][CameraReadyState] current stream set to innov. camera stream');
           streamRef.current = innovatricsCameraRef.current?.getVideoElement();
         }
         setIsCameraRunning(true);
        
        if (timeoutRef.current) {
          console.log('[BackCapture][CameraReadyState] Clearing fallback timeout');
          clearTimeout(timeoutRef.current);
        }
      }else if (event?.detail?.appState === 'ERROR') {
        console.log('[BackCapture][CameraReadyState] Error occured during camera initialisation . setting cameraRunning state to true so that other buttons should be enabled');
        setIsCameraRunning(true);
      }
    };
  
    console.log('[BackCapture][CameraReadyState] Adding event listener...');
    document.addEventListener(
      'document-auto-capture:state-changed', 
      handleStateChanged
    );
  
    return () => {
      console.log('[BackCapture][CameraReadyState] Cleaning up...');
      if (timeoutRef.current) {
        console.log('[BackCapture][CameraReadyState] Clearing pending timeout');
        clearTimeout(timeoutRef.current);
      }
      document.removeEventListener(
        'document-auto-capture:state-changed',
        handleStateChanged
      );
    };
  }, []);

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const handleDocumentPhotoTaken = async ({ image, data }, content) => {
    if (image !== undefined) {
      setCapturingMode("AUTO")
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result;
        console.log('[BackCapture][Camera] Image successfully converted to base64, length:', base64Data.length);
        setImageUrl(base64Data);
      };
      reader.readAsDataURL(image);
    }
  };

  const handleAppState = (event) => {
    setAppState(event?.detail?.appState);
  };

  const instructionChangeEvent = "document-auto-capture:instruction-changed";

  useEffect(() => {
    document.addEventListener(
      DocumentCustomEvent.STATE_CHANGED,
      handleAppState
    );

    if (isMobile) return;

    const handleInstructionChange = (event) => {
      setCaptureStatus(event.detail.instructionCode);
    };

    document.addEventListener(instructionChangeEvent, handleInstructionChange);

    return () => {
      document.removeEventListener(
        instructionChangeEvent,
        handleInstructionChange
      );
      document.removeEventListener(
        DocumentCustomEvent.STATE_CHANGED,
        handleAppState
      );
    };
  }, []);

  useEffect(() => {
    async function startCamera() {
      try {
        stopSafeStreamFromRef();
        const videoConstraints = {
          facingMode: facingMode,
        };

        const stream = await navigator.mediaDevices.getUserMedia({
          video: videoConstraints,
        });
        console.log('[BackCapture][startCamera] current stream set to manual camera stream');
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream; // Set the video stream to the video element
        }
      } catch (error) {
        console.error("Error accessing camera:", error);
      }
    }

    if (captureMode === "manual") startCamera();
  }, [facingMode, captureMode]); // Only trigger the camera start when facingMode changes

  const captureImage = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext("2d");

    // Set canvas dimensions to video dimensions
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    setCapturingMode("MANUAL")
    // Flip the context horizontally to mirror the video
    // context.translate(canvas.width, 0);
    // context.scale(-1, 1);

    // Draw the current video frame onto the canvas (flipped)
    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    // Convert the canvas content to a data URL (image) and store it

    canvas.toBlob((blob) => {
      const reader = new FileReader();
      reader.onload = () => {
        const base64Data = reader.result;
        console.log('[BackCapture][Manual Capture] Blob successfully converted to base64, length:', base64Data.length);
        setImageUrl(base64Data);
      };
      reader.readAsDataURL(blob);
    }, "image/png");
  };

  useEffect(() => {
    if (imageUrl) {
      backImageRef.current=imageUrl;
      console.log('[BackCapture] backImage ref set, navigating to ImageCheck');

      stepperFunc(steps[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [imageUrl]);

  const handleSkip = () => {
    stepperFunc(steps[0] + 1);
  };

  // Set loadTiming when FlipScreen is dismissed to start measuring autocapture loading time
  useEffect(() => {
    if (isFlipScreenProcessed) {
      setLoadTiming(performance.now());
      console.log('[BackCapture][FlipScreen] FlipScreen dismissed, starting load timing measurement for autocapture');
    }
  }, [isFlipScreenProcessed]);

  if(!isFlipScreenProcessed) return <FlipScreen setIsFlipScreenProcessed={setIsFlipScreenProcessed} stepperFunc={stepperFunc} steps={steps}/>;


  if (!imageUrl && isLoading) return <Loader />;

  return (
    <>
      <>
        <Head
          countryCode={countryCode}
          showBackIcon
          handleBack={() => {
            sessionStorage.removeItem('isFlipScreenShown');
            stepperFunc(steps[2]);
          }}
          showLanguages={false}
          style={{ zIndex: "100" }}
          stepperFunc={stepperFunc}
        />
        <div className="parent-container" data-testid="back-capture" style={{ paddingTop: 0 }}>
          <div className="main">
            <div
              className="left-section"
              style={{ flexDirection: "column", background: "black" }}
            >
              <CameraLoadingOverlay isVisible={!isCameraRunning} />
              <>
                {isLoading ? (
                  <loader />
                ) : captureMode === "manual" ? (
                  <div
                    style={{ position: "relative", marginBottom: 40 }}
                    className="auto-capture"
                  >
                    <div
                      className="video-container"
                      style={{
                        width: "100%",
                        height:
                          width <= 767
                            ? "100%"
                            : "auto" /* Set container height as desired */,
                        overflow: "hidden",
                        marginBottom: 20,
                      }}
                    >
                      {/* {isMobile && (
                        <button
                          className="flip"
                          onClick={() => flipCamera()}
                          style={{ marginTop: "10px" }}
                        >
                          <span
                            class="material-symbols-outlined"
                            style={{ color: "white" }}
                          >
                            flip_camera_ios
                          </span>
                        </button>
                      )} */}
                      <video
                        ref={videoRef}
                        autoPlay
                        playsInline
                        style={{
                          width: "100%",
                          height: "auto",
                          objectFit: "cover",
                          transform:
                            facingMode === "environment"
                              ? "none"
                              : "scaleX(-1)",
                        }}
                      />
                      <canvas ref={canvasRef} style={{ display: "none" }} />
                    </div>

                    <div
                      style={{
                        position: "absolute",
                        width: "100%",
                        height: "100%",
                        top: 0,
                        left: 0,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                      }}
                    >
                      <div
                        style={{
                          borderColor: "white",
                          borderWidth: 2,
                          height: 200,
                          width: 300,
                          marginBottom: "20px",
                        }}
                      ></div>
                    </div>
                  </div>
                ) : (
                  <div
                    style={{ position: "relative" }}
                    className="auto-capture"
                  >
                    <DocumentCamera
                      ref={innovatricsCameraRef}
                      cameraFacing={facingMode}
                      onPhotoTaken={handleDocumentPhotoTaken}
                      placeholder="id-solid"
                      backdropColor="none"
                    />
                    <DocumentUI
                      theme={{
                        colors: {
                          placeholderColor: "white",
                          instructionColorSuccess: "#00FF83",
                        },
                      }}
                      placeholder="id-solid"
                      backdropColor="rgba(10, 10, 10, 0.5)"
                      instructions={{}}
                      appStateInstructions={{
                        loading: {
                          text: "",
                          visible: false,
                        },
                      }}
                    />
                  </div>
                )}
              </>
              {width > 767 &&
                (captureMode === "auto" ? (
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: 500,
                      textAlign: "center",
                      position: "absolute",
                      bottom: "40px",
                    }}
                  >
                    <div style={{ color: "#919191" }}>
                      {languageData[language]?.documentCapture?.stillNotWorking ??
                        languageData["en"]?.documentCapture?.stillNotWorking}
                    </div>
                    <div
                      style={{
                        textDecorationLine: "underline",
                        color: "white",
                        cursor: "pointer",
                      }}
                      onClick={() => setCaptureMode("manual")}
                    >
                      {languageData[language]?.documentCapture?.clickToCaptureInstead ??
                        languageData["en"]?.documentCapture?.clickToCaptureInstead}
                    </div>
                  </div>
                ) : (
                  <div
                    onClick={() => captureImage()}
                    style={{
                      position: "absolute",
                      bottom: 24,
                      cursor: "pointer",
                    }}
                  >
                    <img src={captureDocButton} alt="button_link" />
                  </div>
                ))}
            </div>
            <div
              className="right-section md:items-start items-center pt-[18px] md:pt-[32px]"
              style={{
                paddingTop: width <= 767 ? "18px" : "32px",
                // background: width <= 767 ? "#000000" : "white",
                // color: width > 767 ? "#000000" : "white",
                fontFamily: "Inter, sans-serif",
              }}
            >
              {width > 767 ? (
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    alignItems: "center",
                    height: "100%",
                  }}
                >
                  <div>
                    <div className="flex flex-col md:mt-[80px]">
                      <div className="flex justify-center md:justify-start">
                        <svg
                          width="44"
                          height="44"
                          viewBox="0 0 44 44"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <circle
                            cx="22"
                            cy="22"
                            r="22"
                            fill={bgColor}
                            style={{ background: bgColor }}
                          />
                          <g clip-path="url(#clip0_1556_28470)">
                            <path
                              d="M15 25.5H13V31.5H19V29.5H15V25.5ZM15 15.5H19V13.5H13V19.5H15V15.5ZM31 13.5H25V15.5H29V19.5H31V13.5ZM29 29.5H25V31.5H31V25.5H29V29.5ZM22 18.5C19.79 18.5 18 20.29 18 22.5C18 24.71 19.79 26.5 22 26.5C24.21 26.5 26 24.71 26 22.5C26 20.29 24.21 18.5 22 18.5ZM22 24.5C20.9 24.5 20 23.6 20 22.5C20 21.4 20.9 20.5 22 20.5C23.1 20.5 24 21.4 24 22.5C24 23.6 23.1 24.5 22 24.5Z"
                              fill="#1658aa"
                              style={{ fill: color }}
                            />
                          </g>
                          <defs>
                            <clipPath id="clip0_1556_28470">
                              <rect
                                width="24"
                                height="24"
                                fill="white"
                                transform="translate(10 10.5)"
                              />
                            </clipPath>
                          </defs>
                        </svg>
                      </div>
                      <div
                        className="mt-6 md:mt-4 text-[18px] md:text-[26px] font-semibold text-[#1658aa] text-center md:text-left mb-6 md:mb-0 "
                        style={{ color: color }}
                      >
                        {languageData[language]?.backDocCapture
                          ?.heading +
                          " " +
                          (documentName == "Resident Id Card"
                            ? residentIDCardName?.[countryCode]
                              ? residentIDCardName?.[countryCode]
                              : "Identity Card"
                            : documentName == "Passport"
                            ? `${countryName} Passport`
                            : documentName == "Driving License"
                            ? drivingLicenceName?.[countryCode]
                              ? drivingLicenceName?.[countryCode]
                              : documentName
                            : documentName == "Id Card"
                            ? idCardName?.[countryCode]
                              ? idCardName?.[countryCode]
                              : documentName
                            : documentName) +
                          " " +
                          (languageData[language]?.frontDocCapture?.headingBack
                            ? languageData[language]?.frontDocCapture
                                ?.headingBack
                            : languageData["en"]?.frontDocCapture?.headingBack)}
                      </div>
                    </div>
                  </div>

                  <div className="w-full md:mt-[30px]">     
                    {documentCapture?.uploadAllowed && (
                      <div className={`flex flex-col md:justify-end items-center md:gap-[12px] w-full ${
                        documentSideConfiguration === documentSideConfigurationConstants.FRONT_MANDATORY_BACK_MANDATORY ? "md:mt-16" : "md:mt-2"
                      }`}>
                      
                        <div
                          style={{
                            color: "#919191",
                            fontSize: 12,
                            fontWeight: 500,
                          }}
                          >
                            {languageData[language]?.documentCapture?.fileFormats ??
                              languageData["en"]?.documentCapture?.fileFormats}
                          </div>
                        <button
                          className="flex items-center justify-center  gap-[8px] w-full pt-[12px] pb-[12px] pr-[24px] pl-[24px] text-white rounded-[4px] text-[14px] font-medium mt-3 md:mt-[0px]"
                          style={{
                            borderColor: color,
                            borderWidth: 1,
                            color: color,
                          }}
                          onClick={() => fileInputRef.current.click()}
                        >
                          {languageData[language]?.backDocCapture?.uploadButton ??
                            languageData["en"]?.backDocCapture?.uploadButton}{" "}
                          <GoogleIcon
                            iconName="upload"
                            size={18}
                            style={{ color: color }}
                          />
                        </button>
                      </div>
                    )}
                    <div className={`${documentSideConfiguration === documentSideConfigurationConstants.FRONT_MANDATORY_BACK_MANDATORY ? "" : "md:mt-[12px]"}`}>
                      <button
                        onClick={handleSkip}
                        className="flex flex-row items-center justify-center md:gap-4 gap-3 w-full text-white py-4 px-4 rounded mt-3 md:mt-[0px] "
                        style={{
                          backgroundColor: appState ? color : bgColor,
                         display: documentSideConfiguration === documentSideConfigurationConstants.FRONT_MANDATORY_BACK_MANDATORY ? "none" : "flex"
                        }}
                        disabled={!appState}
                      >
                        {languageData[language]?.backDocCapture?.skipButton ??
                          languageData["en"]?.backDocCapture?.skipButton}
                      </button>
                    </div>
                  </div>
                  <Footer />
                </div>
              ) : (
                <MobileCapture
                  currentDocumentStep={currentDocumentStep}
                  uploadAllowed={documentCapture?.uploadAllowed}
                  color={color}
                  documentSideConfiguration={documentSideConfiguration}
                  journey={journey}
                  docType="back"
                  captureMode={captureMode}
                  switchToManual={() => {
                    setCaptureMode("manual");
                  }}
                  captureClick={() => {
                    captureImage();
                  }}
                  uploadClick={() => {
                    try {
                      if (!fileInputRef.current) {
                        console.error("[BackCapture][uploadClick] File input ref is null!");
                        return;
                      }
                      console.log("[BackCapture][uploadClick]Triggering file input...");
                      fileInputRef.current.click();
                    } catch (error) {
                      console.error("[BackCapture][uploadClick] Failed to open file picker(Format-1):", (JSON.stringify(error)));
                      console.error("[BackCapture][uploadClick] Failed to open file picker(Format-2):",JSON.stringify({
                        name: error?.name,
                        message: error?.message,
                        stack: error?.stack
                      }));
                    
                    }
                  }}
                  skipClick={() => handleSkip()}
                />
              )}
            </div>
          </div>
        </div>
      </>

      {isModalOpen && (
        <div className="modal">
          <div className="modal-content">
            <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
              <GoogleIcon
                iconName="cancel"
                size={40}
                style={{ color: "#F14437" }}
                className="mb-4"
              />
            </div>
            <div>
              {" "}
              {languageData[language]?.failedUpload?.title ||
                UploadStatusMessages.FailedUploadTitle}
            </div>
            <p className="mt-4">{message}</p>
            <button
              className="px-8 py-4 mt-12 md:mt-20"
              style={{
                border: "1px solid rgba(0, 0, 0, 0.10)",
                position: "relative",
                borderRadius: 4,
                width: "100%",
                display: "flex",
                alignItems: "center",
                flexDirection: "row",
                justifyContent: "center",
                background: color,
                color: "white",
              }}
              onClick={handleClose}
            >
              {languageData[language]?.failedUpload?.btn ||
                UploadStatusMessages.FailedUploadButton}
            </button>
          </div>
        </div>
      )}

      {/* upload file */}
      <div style={{ 
        position: 'absolute',
        width: '1px',
        height: '1px',
        padding: 0,
        margin: '-1px',
        overflow: 'hidden',
        clip: 'rect(0, 0, 0, 0)',
        whiteSpace: 'nowrap',
        border: 0
      }}>
        <input
          type="file"
          ref={fileInputRef}
          accept=".jpg, .png, .jpeg, .pdf"
          onChange={handleFileChange}
        />
      </div>
    </>
  );
};

export default BackDocumentCapture;